import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSuperadminPlatform } from '../hooks/trSuperadminPlatform';
import { ArrowLeft, Info } from 'lucide-react';

// ─── Tooltip ──────────────────────────────────────────────────────────────────

function InfoTooltip({ text }) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const ref = useRef(null);

  const handleEnter = () => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 6, left: r.left + r.width / 2 });
    }
    setVisible(true);
  };

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={handleEnter}
        onMouseLeave={() => setVisible(false)}
        className="inline-flex items-center ml-1 cursor-help"
      >
        <Info size={12} className="text-gray-400 hover:text-gray-600 transition-colors" />
      </span>
      {visible && (
        <div
          style={{ position: 'fixed', top: coords.top, left: Math.max(8, coords.left - 112), zIndex: 9999 }}
          className="w-56 rounded-lg bg-gray-900 text-white text-xs px-3 py-2 shadow-xl leading-relaxed pointer-events-none"
        >
          {text}
        </div>
      )}
    </>
  );
}

// ─── Platform Usage (Supabase) ────────────────────────────────────────────────

const PLAN_COLORS = {
  free: 'bg-gray-100 text-gray-600',
  pro: 'bg-blue-100 text-blue-700',
  team: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-yellow-100 text-yellow-700',
};

function PlatformUsage({ data, loading }) {
  const sb = data?.supabase;
  const pct = sb?.db_usage_pct ?? 0;
  const barColor = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-orange-400' : 'bg-indigo-500';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <span>⚡</span> Supabase
          {sb?.project_name && <span className="text-gray-400 font-normal text-sm">— {sb.project_name}</span>}
        </h2>
        <div className="flex items-center gap-2">
          {sb?.plan && (
            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded uppercase ${PLAN_COLORS[sb.plan] ?? PLAN_COLORS.free}`}
            >
              {sb.plan}
            </span>
          )}
          {sb?.status && (
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${sb.status === 'ACTIVE_HEALTHY' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}
            >
              {sb.status === 'ACTIVE_HEALTHY' ? '● Healthy' : sb.status}
            </span>
          )}
        </div>
      </div>

      {loading && <div className="h-24 bg-gray-50 rounded-lg animate-pulse" />}

      {!loading && sb?.available && (
        <div className="space-y-5">
          {/* DB size */}
          <div>
            <div className="flex items-end justify-between mb-1.5">
              <span className="text-sm font-medium text-gray-700 flex items-center">
                Taille base de données
                <InfoTooltip text="Espace disque utilisé par votre base PostgreSQL. Inclut les index et les données. La limite dépend de votre plan Supabase." />
              </span>
              <span className="text-sm font-bold text-gray-900">
                {sb.db_size_pretty}
                {sb.db_limit_pretty && <span className="text-gray-400 font-normal"> / {sb.db_limit_pretty}</span>}
                {sb.db_usage_pct != null && (
                  <span
                    className={`ml-2 text-xs font-semibold ${pct >= 90 ? 'text-red-600' : pct >= 70 ? 'text-orange-500' : 'text-gray-500'}`}
                  >
                    ({pct}%)
                  </span>
                )}
              </span>
            </div>
            <div className="h-2.5 rounded-full bg-gray-100">
              <div
                className={`h-2.5 rounded-full transition-all ${barColor}`}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
          </div>

          {/* Counts + connexions */}
          {(() => {
            const TOOLTIPS = {
              schools: "Nombre total d'écoles enregistrées sur la plateforme.",
              users: 'Comptes administrateurs ayant accès au dashboard web.',
              students: "Nombre total d'élèves inscrits sur toutes les écoles.",
              parents: 'Parents avec un compte app mobile actif.',
              trips: 'Trajets créés (toutes écoles confondues, tous statuts).',
              notifications: 'Notifications push envoyées depuis la création du projet.',
            };
            return (
              <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
                {sb.counts &&
                  Object.entries(sb.counts).map(([key, val]) => (
                    <div key={key} className="text-center p-2 rounded-lg bg-gray-50 border border-gray-100">
                      <p className="text-xs text-gray-400 capitalize mb-0.5 flex items-center justify-center gap-0.5">
                        {key}
                        {TOOLTIPS[key] && <InfoTooltip text={TOOLTIPS[key]} />}
                      </p>
                      <p className="text-sm font-bold text-gray-800">{val.toLocaleString()}</p>
                    </div>
                  ))}
                {sb.active_connections != null && (
                  <div className="text-center p-2 rounded-lg bg-blue-50 border border-blue-100">
                    <p className="text-xs text-blue-400 mb-0.5 flex items-center justify-center gap-0.5">
                      Connexions
                      <InfoTooltip text="Requêtes SQL en cours d'exécution sur la DB à cet instant (pg_stat_activity)." />
                    </p>
                    <p className="text-sm font-bold text-blue-800">{sb.active_connections}</p>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Top tables */}
          {sb.top_tables?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                Tables les plus lourdes
                <InfoTooltip text="Taille totale de chaque table (données + index). Utile pour identifier les tables candidates à l'archivage." />
              </p>
              <div className="space-y-1.5">
                {sb.top_tables.map((t) => {
                  const tpct = sb.db_size_bytes > 0 ? Math.round((t.size_bytes / sb.db_size_bytes) * 100) : 0;
                  return (
                    <div key={t.name} className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 w-36 truncate">{t.name}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-gray-100">
                        <div className="h-1.5 rounded-full bg-indigo-300" style={{ width: `${tpct}%` }} />
                      </div>
                      <span className="text-xs text-gray-500 w-14 text-right">{t.size}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && sb && !sb.available && (
        <p className="text-xs text-gray-400">{sb.error ?? 'Données indisponibles'}</p>
      )}
    </div>
  );
}

// ─── Firebase Section ─────────────────────────────────────────────────────────

function FirebaseSection({ data, loading }) {
  const fb = data?.firebase;

  if (loading)
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <div className="h-16 bg-gray-50 rounded-lg animate-pulse" />
      </div>
    );

  if (!fb) return null;

  const platforms = Object.entries(fb.by_platform ?? {});
  const totalPlatform = platforms.reduce((s, [, v]) => s + v, 0);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
      <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
        <span>🔔</span> Firebase FCM
        <InfoTooltip text="Données issues de vos propres tables (device_tokens + notifications). Aucune credential Firebase supplémentaire requise." />
      </h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="text-center p-3 rounded-lg bg-orange-50 border border-orange-100">
          <p className="text-xs text-orange-500 mb-1 flex items-center justify-center gap-0.5">
            Tokens actifs
            <InfoTooltip text="Appareils avec un token FCM valide (is_active=true). Représente la couverture push notifications." />
          </p>
          <p className="text-xl font-bold text-orange-800">{fb.active_tokens}</p>
          {fb.inactive_tokens > 0 && (
            <p className="text-xs text-orange-400 mt-0.5">
              {fb.inactive_tokens} inactif{fb.inactive_tokens > 1 ? 's' : ''}
            </p>
          )}
        </div>

        <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100">
          <p className="text-xs text-gray-400 mb-1 flex items-center justify-center gap-0.5">
            Taux livraison 7j
            <InfoTooltip text="(sent + delivered + read) / (total non-pending) sur les 7 derniers jours. Null si aucune notification envoyée." />
          </p>
          <p
            className={`text-xl font-bold ${
              fb.delivery_rate_7d === null
                ? 'text-gray-400'
                : fb.delivery_rate_7d >= 95
                  ? 'text-green-700'
                  : fb.delivery_rate_7d >= 80
                    ? 'text-orange-600'
                    : 'text-red-600'
            }`}
          >
            {fb.delivery_rate_7d !== null ? `${fb.delivery_rate_7d}%` : '—'}
          </p>
        </div>

        <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100">
          <p className="text-xs text-gray-400 mb-1 flex items-center justify-center gap-0.5">
            Notifs 24h
            <InfoTooltip text="Notifications créées dans les dernières 24 heures, tous statuts confondus." />
          </p>
          <p className="text-xl font-bold text-gray-800">{fb.notifs_24h.toLocaleString()}</p>
        </div>

        <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100">
          <p className="text-xs text-gray-400 mb-1 flex items-center justify-center gap-0.5">
            Échecs 7j
            <InfoTooltip text="Notifications avec status=failed sur 7 jours. Un taux élevé indique des tokens FCM expirés à purger." />
          </p>
          <p className={`text-xl font-bold ${fb.failed_7d > 0 ? 'text-red-600' : 'text-green-700'}`}>{fb.failed_7d}</p>
        </div>
      </div>

      {platforms.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
            Répartition plateformes
            <InfoTooltip text="Distribution des tokens FCM actifs par OS mobile." />
          </p>
          <div className="flex gap-3">
            {platforms.map(([platform, count]) => {
              const pct = totalPlatform > 0 ? Math.round((count / totalPlatform) * 100) : 0;
              const color =
                platform === 'android' ? 'bg-green-500' : platform === 'ios' ? 'bg-blue-500' : 'bg-gray-400';
              return (
                <div key={platform} className="flex items-center gap-2 flex-1">
                  <div className="flex-1 h-2 rounded-full bg-gray-100">
                    <div className={`h-2 rounded-full ${color}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs text-gray-600 whitespace-nowrap capitalize">
                    {platform} {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Notification Queue ───────────────────────────────────────────────────────

function NotificationQueueSection({ data, loading }) {
  const q = data?.notification_queue;

  if (loading)
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <div className="h-16 bg-gray-50 rounded-lg animate-pulse" />
      </div>
    );

  if (!q) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
      <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
        <span>📬</span> File de notifications
        <InfoTooltip text="État de la notification_queue. Les jobs pending attendent d'être envoyés par le worker. Un volume élevé de failed indique un problème FCM." />
      </h2>
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100">
          <p className="text-xs text-gray-400 mb-1">Total en queue</p>
          <p className="text-xl font-bold text-gray-800">{q.total.toLocaleString()}</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-yellow-50 border border-yellow-100">
          <p className="text-xs text-yellow-600 mb-1 flex items-center justify-center gap-0.5">
            En attente
            <InfoTooltip text="Notifications en attente de traitement par le NotificationWorker." />
          </p>
          <p className="text-xl font-bold text-yellow-800">{q.pending}</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-100">
          <p className="text-xs text-gray-400 mb-1 flex items-center justify-center gap-0.5">
            Échouées
            <InfoTooltip text="Jobs en statut failed. Si > 0 et croissant, vérifier les credentials FCM et les logs du worker." />
          </p>
          <p className={`text-xl font-bold ${q.failed > 0 ? 'text-red-600' : 'text-green-700'}`}>{q.failed}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Stale Tokens ─────────────────────────────────────────────────────────────

function StaleTokensSection({ data, loading, onPurge }) {
  const [purging, setPurging] = useState(false);
  const [purged, setPurged] = useState(null);
  const fb = data?.firebase;

  if (loading || !fb) return null;
  if (fb.stale_tokens === 0) return null;

  const handlePurge = async () => {
    setPurging(true);
    try {
      const result = await onPurge();
      setPurged(result.deleted);
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-orange-200 p-5 mb-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-orange-800 flex items-center gap-2">
            <span>🗑️</span> Tokens FCM expirés
            <InfoTooltip text="Tokens FCM inactifs depuis plus de 30 jours. Les purger allège les requêtes et améliore le taux de livraison effectif." />
          </h2>
          <p className="text-sm text-orange-600 mt-1">
            {fb.stale_tokens} token{fb.stale_tokens > 1 ? 's' : ''} inactif{fb.stale_tokens > 1 ? 's' : ''} depuis plus
            de 30 jours
          </p>
          {purged !== null && (
            <p className="text-xs text-green-600 mt-1 font-medium">
              ✓ {purged} token{purged > 1 ? 's' : ''} supprimé{purged > 1 ? 's' : ''}
            </p>
          )}
        </div>
        <button
          onClick={handlePurge}
          disabled={purging || purged !== null}
          className="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
        >
          {purging ? 'Purge…' : purged !== null ? 'Purgé' : 'Purger'}
        </button>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SuperadminPlatform() {
  const navigate = useNavigate();
  const { platformUsage, platformLoading, handlePurgeStaleTokens, error } = useSuperadminPlatform();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate('/superadmin')}
          className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-sm transition-colors"
        >
          <ArrowLeft size={16} />
          Dashboard
        </button>
        <span className="text-gray-300">/</span>
        <h1 className="text-xl font-bold text-gray-900">Infrastructure</h1>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div>
      )}

      <PlatformUsage data={platformUsage} loading={platformLoading} />
      <FirebaseSection data={platformUsage} loading={platformLoading} />
      <StaleTokensSection data={platformUsage} loading={platformLoading} onPurge={handlePurgeStaleTokens} />
      <NotificationQueueSection data={platformUsage} loading={platformLoading} />
    </div>
  );
}
